import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Rss, Shield, Newspaper, ExternalLink, ChevronDown, ChevronRight, Globe } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";

/* ============================================================
   DNH — Public Sources Directory (/sources)
   Browse all 300+ news sources by country.
   3 source types: RSS (all) · Official/Auth · Publications
   ============================================================ */

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── Source type configs ───────────────────────────────────────
const TYPE_CFG = {
  official:    { label: "Official",    icon: <Shield size={11} />,    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  publication: { label: "Publication", icon: <Newspaper size={11} />, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const RSS_BADGE = { label: "RSS", icon: <Rss size={11} />, cls: "bg-blue-50 text-blue-700 border-blue-200" };

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CFG[type] || TYPE_CFG.publication;
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-[4px] border font-mono text-[9.5px] uppercase tracking-[0.06em] ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

const RssBadge = () => (
  <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-[4px] border font-mono text-[9.5px] uppercase tracking-[0.06em] ${RSS_BADGE.cls}`}>
    {RSS_BADGE.icon}{RSS_BADGE.label}
  </span>
);

const CatBadge = ({ label }) => (
  <span className="inline-flex items-center h-5 px-2 rounded-[4px] border border-[var(--hairline)] bg-[var(--surface-2)] font-mono text-[9px] uppercase tracking-[0.06em] text-[var(--muted)]">
    {label}
  </span>
);

// ── Source card ───────────────────────────────────────────────
const SourceCard = ({ source }) => {
  const href = `/sources/${encodeURIComponent(source.source_id)}`;
  const webUrl = source.web_url || "#";

  return (
    <div
      className="group flex flex-col sm:flex-row items-start justify-between gap-3 px-4 py-3 border-b border-[var(--hairline)] last:border-b-0 hover:bg-[var(--surface-2)] transition-colors"
      data-testid={`source-card-${source.source_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <a
            href={href}
            data-testid={`source-card-title-${source.source_id}`}
            className="font-serif text-[14px] text-[var(--ink)] hover:text-[var(--crimson)] transition-colors leading-snug break-words max-w-full"
          >
            {source.name}
          </a>
          <RssBadge />
          <TypeBadge type={source.source_type} />
          <CatBadge label={source.category_label} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {source.articles_today > 0 && (
            <span className="font-mono text-[10.5px] text-[var(--muted)]">
              {source.articles_today} today
            </span>
          )}
          {source.story_count > 0 && (
            <span
              className="inline-flex items-center h-5 px-2 rounded-[4px] bg-[var(--surface-2)] border border-[var(--hairline)] font-mono text-[9.5px] text-[var(--ink-2)]"
              title="Total stories indexed from this source"
              data-testid={`source-card-count-${source.source_id}`}
            >
              {source.story_count.toLocaleString()} stories
            </span>
          )}
          {source.credibility_score > 0 && (
            <span className="font-mono text-[10px] text-[var(--muted)]">
              Cred {source.credibility_score}/100
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 mt-0.5">
        <a
          href={href}
          data-testid={`source-card-stories-${source.source_id}`}
          className="inline-flex items-center gap-1 min-h-9 px-3 rounded-full border border-[var(--hairline)] font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--muted)] hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          View Stories
        </a>
        <a
          href={webUrl}
          data-testid={`source-card-website-${source.source_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="min-h-9 min-w-9 inline-flex items-center justify-center text-[var(--muted)] hover:text-[var(--crimson)] transition-colors sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          title="Visit publication"
        >
          <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
};

// ── Country section ───────────────────────────────────────────
const CountrySection = ({ country }) => {
  const [expanded, setExpanded] = useState(true);
  const hasOfficial = country.official_auth.length > 0;
  const hasPubs     = country.publications.length > 0;

  return (
    <div className="border border-[var(--hairline)] rounded-[8px] overflow-hidden mb-4" data-testid={`country-section-${country.country_slug}`}>
      {/* Country header */}
      <button
        className="w-full flex items-start justify-between gap-3 px-5 py-3.5 bg-[var(--surface-2)] hover:bg-[var(--surface-2)]/80 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        data-testid={`country-section-toggle-${country.country_slug}`}
      >
        <div className="flex flex-wrap min-w-0 items-center gap-x-3 gap-y-2">
          <Globe size={14} className="text-[var(--muted)] shrink-0" />
          <span className="font-serif text-[15px] text-[var(--ink)] break-words">{country.country_name}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">
            {country.total} source{country.total !== 1 ? "s" : ""}
          </span>
          <RssBadge />
          {country.official_auth.length > 0 && (
            <span className="font-mono text-[10px] text-amber-600">{country.official_auth.length} Official</span>
          )}
          {country.publications.length > 0 && (
            <span className="font-mono text-[10px] text-emerald-600">{country.publications.length} Publications</span>
          )}
        </div>
        {expanded ? <ChevronDown size={14} className="text-[var(--muted)] shrink-0 mt-1" /> : <ChevronRight size={14} className="text-[var(--muted)] shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div>
          {/* Official / Authoritative sources */}
          {hasOfficial && (
            <div>
              <div className="flex items-center gap-2 px-5 py-2 border-y border-[var(--hairline)] bg-amber-50/40">
                <Shield size={11} className="text-amber-600" />
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-amber-700">
                  Official & Authoritative — {country.official_auth.length}
                </span>
              </div>
              {country.official_auth.map(s => <SourceCard key={s.source_id} source={s} />)}
            </div>
          )}

          {/* Publications */}
          {hasPubs && (
            <div>
              <div className="flex items-center gap-2 px-5 py-2 border-y border-[var(--hairline)] bg-emerald-50/40">
                <Newspaper size={11} className="text-emerald-600" />
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-700">
                  News Publications — {country.publications.length}
                </span>
              </div>
              {country.publications.map(s => <SourceCard key={s.source_id} source={s} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Main Sources page
// ══════════════════════════════════════════════════════════════
export default function SourcesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  // Pre-populate search from ?country= URL param (e.g. from Related Sources "View all →" link)
  const initialQ = searchParams.get("country") ? searchParams.get("country").replace(/-/g, " ") : "";
  const [q, setQ]           = useState(initialQ);
  const [debouncedQ, setDebouncedQ] = useState(initialQ);
  const [typeFilter, setTypeFilter] = useState("all"); // all | official | publication

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const countryParam = searchParams.get("country");
      if (countryParam) {
        // Direct country slug filter — exact match, no text search needed
        params.set("country", countryParam);
      } else if (debouncedQ) {
        params.set("q", debouncedQ);
      }
      const r = await fetch(`${BASE}/api/sources/browse?${params}`);
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, searchParams]);

  useEffect(() => { load(); }, [load]);

  const filteredCountries = useMemo(() => {
    if (!data?.countries) return [];
    if (typeFilter === "all") return data.countries;
    return data.countries
      .map(c => ({
        ...c,
        official_auth: typeFilter === "official" ? c.official_auth : [],
        publications:  typeFilter === "publication" ? c.publications : [],
        total: typeFilter === "official" ? c.official_auth.length : c.publications.length,
      }))
      .filter(c => c.total > 0);
  }, [data, typeFilter]);

  const summary = data?.summary || {};

  const headerUser = user ? {
    name: user.name || user.email,
    initials: (user.name || user.email || "U").slice(0, 2).toUpperCase(),
    email: user.email,
    avatar_url: user.avatar_url || null,
    subscription_tier: user.subscription_tier,
    is_admin: user.is_admin,
    plan: user.subscription_tier,
  } : null;

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SiteHeader activeNav="Sources" user={headerUser} ticker />

      {/* Hero */}
      <section className="border-b border-[var(--hairline)]">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-10 lg:pt-14 pb-8">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--crimson)]">Sources</span>
                <span className="text-[var(--hairline-strong)]">·</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--muted)]">
                  {summary.total_sources || "…"} feeds across {summary.total_countries || "…"} countries
                </span>
              </div>
              <h1 className="font-serif text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.01em] text-[var(--ink)]">
                The source network.
              </h1>
              <p className="font-sans text-[14.5px] text-[var(--muted)] mt-3 max-w-[56ch]">
                Every story on DNH comes from one of these RSS-ingested sources.
                Browse by country, find your preferred publications, and read their latest highlights.
              </p>
            </div>
            {/* Legend */}
            <div className="flex flex-col gap-2 font-mono text-[11px] text-[var(--muted)]">
              <div className="flex items-center gap-2">
                <Rss size={12} className="text-blue-600" />
                <span><strong className="text-blue-700">RSS</strong> — {summary.rss_all || summary.total_sources || "…"} sources (all feeds)</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={12} className="text-amber-600" />
                <span data-testid="sources-official-legend"><strong className="text-amber-700">Official</strong> — {summary.official_auth ?? "…"} government, institutional & public authority feeds</span>
              </div>
              <div className="flex items-center gap-2">
                <Newspaper size={12} className="text-emerald-600" />
                <span><strong className="text-emerald-700">Publications</strong> — {summary.publications || "…"} editorial outlets</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <div className="sticky top-[calc(var(--site-header-height)+var(--ticker-strip-height))] z-20 bg-[var(--paper)] border-b border-[var(--hairline)]" data-testid="sources-toolbar">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 min-h-14 py-3 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2 w-full min-w-0 sm:w-auto sm:flex-1 sm:max-w-[360px] h-9 px-3 rounded-full border border-[var(--hairline)] bg-[var(--surface-2)]">
            <Search size={13} className="text-[var(--muted)] shrink-0" />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by publication or country…"
              data-testid="sources-search-input"
              className="bg-transparent outline-none font-mono text-[12px] text-[var(--ink)] placeholder:text-[var(--muted)] flex-1 min-w-0 w-full"
            />
          </div>
          {/* Type filter */}
          <div className="flex flex-wrap items-center gap-1 min-w-0">
            {[
              { key: "all",         label: "All Sources" },
              { key: "official",    label: "Official / Auth" },
              { key: "publication", label: "Publications" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                data-testid={`source-filter-${f.key}`}
                className={`min-h-9 py-1.5 px-3 whitespace-nowrap rounded-full border font-mono text-[10px] uppercase tracking-[0.08em] transition-colors ${
                  typeFilter === f.key
                    ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]"
                    : "border-[var(--hairline)] text-[var(--muted)] hover:text-[var(--ink)] hover:border-[var(--ink)]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="ml-auto font-mono text-[10.5px] text-[var(--muted)] hidden sm:block">
            {loading ? "Loading…" : `${filteredCountries.length} countries`}
          </span>
        </div>
      </div>

      {/* Main content */}
      <main className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-[var(--muted)] font-mono text-[12px]">
            Loading sources…
          </div>
        ) : filteredCountries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <Globe size={32} className="text-[var(--muted)] opacity-30 mb-3" />
            <p className="font-serif text-[15px] text-[var(--ink)] mb-1">No sources found</p>
            <p className="font-mono text-[11.5px] text-[var(--muted)]">Try a different search or filter</p>
          </div>
        ) : (
          filteredCountries.map(c => (
            <CountrySection key={c.country_slug} country={c} />
          ))
        )}
      </main>

      <SiteFooter variant="minimal" />
    </div>
  );
}
